/**
 * Dimples — you are the golf ball.
 *
 * This file is the wiring: the fixed-timestep clock, the phase machine, and the
 * translation of simulation events into juice. The game itself lives in
 * swing.ts (the tee moment) and ballistics.ts (the payoff).
 */

import * as THREE from 'three/webgpu'
import { Sfx } from './audio'
import * as B from './ballistics'
import { bark, bucketLine } from './barks'
import { Cameras } from './cameras'
import { makeRng } from './rng'
import { buildScene } from './scene'
import {
	apexForCharge,
	buildLaunch,
	clubheadAt,
	createTeeBall,
	flushCharge,
	lieCeiling,
	resetTeeBall,
	resolveContact,
	Swing,
	type SwingEvent,
	stampFor,
	stepTeeBall,
	sweetYFor,
	type TeeBall,
	type TeeEvent,
} from './swing'
import * as T from './tuning'
import type {
	Contact,
	ContactTier,
	FlightState,
	GolferSpec,
	HoleSpec,
	LieSpec,
	Phase,
	ShotResult,
	Surface,
} from './types'
import { type LobbyView, Ui } from './ui'

// ── Renderer ─────────────────────────────────────────────────────────────────

const params = new URLSearchParams(location.search)
const forceWebGL = params.has('gl')

const renderer = new THREE.WebGPURenderer({ antialias: true, forceWebGL })
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setSize(window.innerWidth, window.innerHeight)
const host = document.getElementById('app')
if (!host) throw new Error('Missing #app host element')
host.appendChild(renderer.domElement)
await renderer.init()

const handles = buildScene()
const cameras = new Cameras(window.innerWidth / window.innerHeight)
const ui = new Ui()
const sfx = new Sfx()

// ── Run state ────────────────────────────────────────────────────────────────

const rng = makeRng((Math.random() * 0xffffffff) >>> 0)

type Run = {
	phase: Phase
	golfer: GolferSpec
	/** Who is on the mat and who is behind them. Head of the queue is up next. */
	queue: GolferSpec[]
	swing: Swing
	tee: TeeBall
	flight: FlightState | null
	hp: number
	scuffBias: number
	sunUsed: number
	dayScore: number
	best: number
	skims: number
	lastResult: ShotResult | null
	/** Tier of the strike that launched the current flight, for scoring style. */
	lastTier: ContactTier
	shots: number
	dodges: number
	/** Ducks survived against the *current* golfer. Resets each encounter. */
	nerve: number
	/** Golfers turned down in the bucket. */
	skips: number
	aces: number

	// ── The hole in progress ──
	/** Strokes played on this hole so far. */
	strokes: number
	/** Which hole of the day this is. */
	holeNo: number
	/** Where the ball is sitting, in world XZ. */
	ballAt: { x: number; z: number }
	/** What it is sitting on, and therefore what it can do about it. */
	lie: LieSpec
	/** Mud picked up from bad lies, bending every later flight. */
	mud: number
	/** Holes finished today, and the strokes each took. */
	card: number[]
	/** The hole being played. */
	hole: HoleSpec
}

const BEST_KEY = 'dimples.best'

/**
 * Who walks up next, never the same person twice running.
 *
 * Without the re-roll the queue regularly shows the same name three times and
 * reads as a bug rather than as a quiet afternoon — and worse, it makes waiting
 * pointless, which is the one decision the bucket exists to offer.
 */
function pickGolfer(golden: boolean, avoid?: GolferSpec): GolferSpec {
	for (let i = 0; i < 6; i++) {
		const g = rollGolfer(golden)
		if (g !== avoid) return g
	}
	return rollGolfer(golden)
}

function rollGolfer(golden: boolean): GolferSpec {
	const gary = T.GOLFERS[0]!
	const rick = T.GOLFERS[1]!
	const dana = T.GOLFERS[2]!
	const r = rng.next()
	// Who walks up to the mat. Dana is the rare one and the only one who can ace,
	// and the light going is exactly when she turns up — so the last of the day
	// is both the most valuable time to be waiting and the least affordable.
	if (golden) return r < 0.4 ? dana : r < 0.72 ? rick : gary
	return r < 0.45 ? gary : r < 0.85 ? rick : dana
}

function buildQueue(golden: boolean): GolferSpec[] {
	const q: GolferSpec[] = []
	for (let i = 0; i < T.LOBBY_QUEUE; i++) q.push(pickGolfer(golden, q[i - 1]))
	return q
}

function newRun(): Run {
	const golfer = pickGolfer(false)
	return {
		phase: 'LOBBY',
		golfer,
		queue: buildQueue(false),
		swing: new Swing(golfer, rng),
		tee: createTeeBall(),
		flight: null,
		hp: T.HP_MAX,
		scuffBias: 0,
		sunUsed: 0,
		dayScore: 0,
		best: Number(localStorage.getItem(BEST_KEY) ?? 0),
		skims: 0,
		lastResult: null,
		lastTier: 'GOOD',
		shots: 0,
		dodges: 0,
		nerve: 0,
		skips: 0,
		aces: 0,
		strokes: 0,
		holeNo: 1,
		ballAt: { x: 0, z: 0 },
		lie: T.LIES.tee!,
		mud: 0,
		card: [],
		hole: T.COURSE[0]!,
	}
}

let run = newRun()

// ── Time ─────────────────────────────────────────────────────────────────────

let timescale = 1
let hitstopLeft = 0
let recoverLeft = 0
let recoverFrom = 1
let accumulator = 0
let lastFrame = performance.now()
/** Unscaled seconds until the next automatic phase advance. */
let holdTimer = 0
let pendingLaunch: { contact: Contact } | null = null
/** Simulation seconds since launch, for the flutter term in ballistics. */
let flightTime = 0
/** Set on a dodge: re-address once the follow-through finishes. */
let awaitingReset = false
/** Timescale sampled at the moment of contact, so the launch can ease out of
 *  the slow motion it happened in instead of snapping back to full speed. */
let slowmoAtImpact = 1
/** Progress through the storm-off tantrum, or null when nobody is throwing a
 *  club. The rig's pose envelope peaks in the middle of this range, so it has
 *  to be driven across it — handing it the end value poses nothing at all. */
let tantrumT: number | null = null
const TANTRUM_TIME = 1.6
/**
 * Progress through the hole-in-one ceremony, or null when there isn't one.
 *
 * It runs on unscaled time and owns its own clock rather than the phase timer,
 * because the whole beat is the pause: plonk, then long enough silence that it
 * starts to feel like something has gone wrong, then birdsong and one piano
 * note as the camera climbs out of the hole.
 */
let aceT: number | null = null
let acePiano = false
const ACE_SILENCE = 2.6
const ACE_TOTAL = 8.5
/** Seconds the button has been held in the bucket, toward rolling away. */
let lobbyHeld = 0
/** Unscaled seconds until the next ball in the bucket pipes up. */
let bucketChat = 0
/** How long the button has been down mid-flight, or null when it is not. */
let airHold: number | null = null
/** Whether the landing-window tick has already played for this descent. */
let skipCued = false
/** True when the RESULT beat ends a hole rather than leading to another shot. */
let holeOver = false
/** Seconds left of the pre-shot look down the line, or 0 when there isn't one. */
let aimLeft = 0
/**
 * How long the button has been held while the golfer is still settling over the
 * ball. Held long enough and you simply leave.
 *
 * This is the only thing the ball does that a golfer could not possibly mistake
 * for his own bad swing — a duck he can blame on himself, but a ball rolling
 * off the tee is unmistakably the ball. It is confined to the address so it can
 * never collide with the crouch during a live swing.
 */
let addressHeld = 0
/** False until the button has been seen up once, so a hold carried in from the
 *  previous shot cannot instantly accept or skip the golfer standing there. */
let lobbyArmed = false

// ── Input ────────────────────────────────────────────────────────────────────

/**
 * Whether the button is physically down, tracked separately from whether the
 * ball is crouching. A player who holds through the end of one shot and into
 * the next tee is still holding, and the crouch has to start on its own —
 * making them let go and press again to be understood is just a bug.
 */
let inputDown = false
let debugOn = params.has('debug')
const holding = () => inputDown && run.phase === 'TEE'

function press(): void {
	sfx.unlock()
	inputDown = true
	if (run.phase === 'TEE' || run.phase === 'LOBBY') return
	// Impatience is allowed: a tap cuts the pre-shot look short.
	if (run.phase === 'AIM') {
		aimLeft = Math.min(aimLeft, 0.05)
		return
	}

	// In the air one button carries three verbs, and each fires at the moment it
	// has to. A skip is a timing check, so it resolves on the press itself. A
	// nudge is not, so it waits for the release and lets a longer hold become a
	// dive instead.
	if ((run.phase === 'FLIGHT' || run.phase === 'ROLLOUT') && run.flight) {
		if (B.armSkip(run.flight)) {
			sfx.play('tick', { rate: 1.5 })
			airHold = null
			return
		}
		airHold = 0
		return
	}
	// Tapping through the result screen shortens the wait rather than zeroing the
	// timer, so the advance still runs through the one code path that owns it.
	if (run.phase === 'RESULT') holdTimer = Math.min(holdTimer, 0.01)
}

function release(): void {
	// A short press in the air was a nudge; a long one was a dive, and letting go
	// of a dive just stops it.
	if (airHold !== null && run.flight) {
		if (airHold < T.DIVE_HOLD && B.nudge(run.flight)) {
			sfx.play('hop', { rate: 1.3 })
			handles.burst('sparks', run.flight.pos.x, run.flight.pos.y, run.flight.pos.z)
		}
		airHold = null
		B.setDive(run.flight, false)
	}

	// Letting go in the bucket before the roll-back completes is the accept: a
	// tap takes whoever is on the mat, a hold sends you back to wait.
	if (run.phase === 'LOBBY') {
		if (lobbyArmed && lobbyHeld > 0 && lobbyHeld < T.LOBBY_HOLD_TIME) {
			inputDown = false
			acceptGolfer()
			return
		}
		lobbyHeld = 0
		lobbyArmed = true
		ui.setLobbyHold(0)
	}
	inputDown = false
}

const canvas = renderer.domElement
canvas.style.touchAction = 'none'
canvas.addEventListener('pointerdown', (e) => {
	e.preventDefault()
	canvas.setPointerCapture(e.pointerId)
	press()
})
canvas.addEventListener('pointerup', release)
canvas.addEventListener('pointercancel', release)
window.addEventListener('blur', release)
window.addEventListener('keydown', (e) => {
	if (e.repeat) return
	if (e.code === 'Space' || e.code === 'Enter') {
		// Leave the key alone when a button has focus, or the day-end card can
		// never be dismissed from the keyboard.
		if (document.activeElement instanceof HTMLButtonElement) return
		e.preventDefault()
		press()
	} else if (e.code === 'KeyD') {
		debugOn = !debugOn
		if (!debugOn) ui.setDebug(null)
	} else if (e.code === 'KeyG' && debugOn) {
		// Tuning aid, and a tuning aid only: skipping the cast at will is not
		// something a player should be able to do by leaning on the keyboard.
		const i = (T.GOLFERS.indexOf(run.golfer) + 1) % T.GOLFERS.length
		startEncounter(T.GOLFERS[i]!)
	}
})
window.addEventListener('keyup', (e) => {
	if (e.code === 'Space' || e.code === 'Enter') release()
})

window.addEventListener('resize', () => {
	renderer.setSize(window.innerWidth, window.innerHeight)
	cameras.resize(window.innerWidth, window.innerHeight)
})

// ── Encounter flow ───────────────────────────────────────────────────────────

const goldenHour = () => run.sunUsed >= T.SUN_NOTCHES - T.GOLDEN_HOUR_NOTCHES

function spendSun(n: number): void {
	run.sunUsed = Math.min(T.SUN_NOTCHES, run.sunUsed + n)
	ui.setSun(run.sunUsed, T.SUN_NOTCHES, goldenHour())
	handles.setDaylight(run.sunUsed / T.SUN_NOTCHES)
}

function startEncounter(g: GolferSpec): void {
	run.golfer = g
	run.swing = new Swing(g, rng)
	run.phase = 'TEE'
	run.flight = null
	run.skims = 0
	run.nerve = 0
	awaitingReset = false
	pendingLaunch = null
	hitstopLeft = 0
	holeOver = false
	startHole()
	handles.setRigAt(run.hole.tee.x, run.hole.tee.z, true)
	handles.setBallPos(run.hole.tee.x, run.tee.y, run.hole.tee.z)
	handles.golfer.resetProps()
	handles.golfer.setSpec(g)
	handles.golfer.setAnger(0)
	handles.setTrailVisible(false)
	handles.clearTrail()
	cameras.setPhase('AIM', camCtx())
	ui.setAnger(0, T.ANGER[0]!.name)
	ui.hideLobby()
	ui.hideBark()
	ui.hideDistance()
	const hint = angerHint()
	ui.setHint(
		run.shots === 0
			? 'HOLD to duck · RELEASE to be brave'
			: hint
				? `${g.name} steps up · ${hint}`
				: `${g.name} steps up`,
	)
	timescale = 1
	recoverLeft = 0
}

// ── The bucket ───────────────────────────────────────────────────────────────

/** Map a stat onto 1..5 pips against the range the roster actually spans, so
 *  the readout stays honest if the cast changes. */
function pipsFor(value: number, lo: number, hi: number): number {
	if (hi === lo) return 3
	const t = (value - lo) / (hi - lo)
	return Math.max(1, Math.min(5, Math.round(1 + t * 4)))
}

const SPAN = {
	speed: [
		Math.min(...T.GOLFERS.map((g) => g.speedMult)),
		Math.max(...T.GOLFERS.map((g) => g.speedMult)),
	] as const,
	disp: [
		Math.min(...T.GOLFERS.map((g) => g.dispersionDeg)),
		Math.max(...T.GOLFERS.map((g) => g.dispersionDeg)),
	] as const,
	temper: [
		Math.min(...T.GOLFERS.map((g) => g.temperMult)),
		Math.max(...T.GOLFERS.map((g) => g.temperMult)),
	] as const,
}

function lobbyView(): LobbyView {
	const g = run.queue[0] ?? T.GOLFERS[0]!
	return {
		name: g.name,
		pips: [
			{ label: 'power', value: pipsFor(g.speedMult, SPAN.speed[0], SPAN.speed[1]) },
			// Accuracy runs the other way: less dispersion is more pips.
			{ label: 'accuracy', value: pipsFor(-g.dispersionDeg, -SPAN.disp[1], -SPAN.disp[0]) },
			// Temper is a warning, not a virtue, so it burns.
			{
				label: 'temper',
				value: pipsFor(g.temperMult, SPAN.temper[0], SPAN.temper[1]),
				hot: true,
			},
		],
		tempo: g.tempoMult > 1.05 ? 'slow' : g.tempoMult < 0.95 ? 'quick' : 'even',
		queued: run.queue.slice(1).map((q) => q.name),
	}
}

function enterLobby(): void {
	if (run.sunUsed >= T.SUN_NOTCHES) {
		endDay('The sun went down', 'day over')
		return
	}
	if (run.hp <= 0) {
		endDay('Retired to the shag bag', 'the ball gave everything')
		return
	}

	run.phase = 'LOBBY'
	run.flight = null
	lobbyHeld = 0
	// A button already down when the bucket opens must not count as a decision.
	lobbyArmed = !inputDown
	resetTeeBall(run.tee, run.lie)
	handles.setTrailVisible(false)
	handles.clearTrail()
	handles.setBallSquash(0)
	handles.setBallPos(0, T.BALL_REST_Y, 0)
	timescale = 1
	recoverLeft = 0

	const up = run.queue[0] ?? pickGolfer(goldenHour())
	handles.golfer.resetProps()
	handles.golfer.setSpec(up)
	handles.golfer.setAnger(0)
	handles.golfer.setSwingAngle(T.SWING_ANGLE_ADDRESS)
	cameras.setPhase('TEE', camCtx())
	ui.setAnger(0, T.ANGER[0]!.name)
	ui.hideDistance()
	ui.showLobby(lobbyView())
	ui.showBark(bark(up.id, 'lobby', 0, rng.next()))
	bucketChat = rng.range(3.5, 6)
	ui.setHint(`TAP to let ${up.name} hit you · HOLD to wait (−${T.SUN_COST.skip} daylight)`)
}

function acceptGolfer(): void {
	const up = run.queue.shift() ?? pickGolfer(goldenHour())
	run.queue.push(pickGolfer(goldenHour(), run.queue[run.queue.length - 1]))
	ui.hideLobby()
	startEncounter(up)
}

/** Roll back into the bucket. The next golfer steps up, and the day gets shorter. */
function skipGolfer(): void {
	run.queue.shift()
	run.queue.push(pickGolfer(goldenHour(), run.queue[run.queue.length - 1]))
	run.skips++
	sfx.play('squat', { rate: 0.8 })
	spendSun(T.SUN_COST.skip)
	enterLobby()
}

/** Somebody in the world says something. The ball never does. */
function say(situation: Parameters<typeof bark>[1]): void {
	ui.showBark(bark(run.golfer.id, situation, run.swing.anger, rng.next()))
}

/**
 * You simply leave. He has to stop, sigh, walk over and put you back — which
 * costs daylight and makes him angrier, and unlike a duck there is no version
 * of it he can blame on his own swing.
 */
function rollAway(): void {
	addressHeld = 0
	inputDown = false
	sfx.play('squat', { rate: 0.7 })
	sfx.play('grunt', { rate: 0.8 + 0.1 * run.swing.anger })
	ui.showStamp('YOU LEFT', 'he has to come and get you')
	spendSun(T.ROLL_AWAY_SUN)
	run.dodges++
	const res = run.swing.registerWhiff([])
	if (res.stormedOff) {
		sfx.play('stormOff')
		say('stormOff')
		handles.golfer.dropHat({ x: 0.9, z: -0.9 })
		tantrumT = 0
		spendSun(T.SUN_COST.stormOff)
		run.phase = 'RESULT'
		holeOver = true
		run.card.push(T.MAX_STROKES)
		run.holeNo++
		holdTimer = 2.4
		return
	}
	handles.golfer.setAnger(run.swing.anger)
	ui.setAnger(run.swing.anger, run.swing.angerName)
	say('whiff')
	run.swing.beginAddress(false)
	resetTeeBall(run.tee, run.lie)
}

function angerHint(): string {
	// The cast is named, never gendered — Dana is not a "him", and naming the
	// golfer reads better than any pronoun would anyway.
	return run.dodges === 0 ? 'duck to make them angrier' : ''
}

/** Every encounter ends by going back to the bucket, never straight to a tee. */
function nextEncounter(): void {
	if (run.card.length >= T.COURSE.length) {
		endDay('Round finished', `${T.COURSE.length} holes`)
		return
	}
	enterLobby()
}

const ACES_KEY = 'dimples.aces'
const GOLD_KEY = 'dimples.gold'

/**
 * The one thing the ball has ever wanted. The ceremony owns the clock from
 * here: no distance stamp, no hint, and no automatic advance to the next
 * golfer — the day is over either way, and what is left is the choice.
 */
function beginAceCeremony(): void {
	run.aces++
	run.phase = 'RESULT'
	cameras.setPhase('CUP', camCtx())
	sfx.setDuck(1)
	sfx.play('plonk')
	ui.hideDistance()
	ui.hideLobby()
	ui.setHint('')
	holdTimer = 0
	aceT = 0
	acePiano = false
}

/**
 * The Cup. The first ace ends the run either way; what the player chooses is
 * what it meant.
 *
 * Retiring is the real ending — the ball stops, mounted, and a new one starts
 * from nothing. Keeping rolling carries a gold scuff forward forever, which is
 * cosmetic and always will be: the dream does not become a stat bonus.
 */
function showAceCard(): void {
	run.phase = 'DAY_END'
	const total = Number(localStorage.getItem(ACES_KEY) ?? 0) + 1
	localStorage.setItem(ACES_KEY, String(total))

	ui.showCard({
		title: 'A hole in one',
		sub: total === 1 ? 'the first one' : `number ${total}`,
		stats: [
			['Struck by', run.golfer.name],
			['Carry and roll', `${Math.round(run.lastResult?.total ?? 0)} m`],
			['Day score', Math.round(run.dayScore).toLocaleString()],
			['Condition left', `${Math.max(0, Math.round(run.hp))}%`],
		],
		button: 'Retire',
		onClick: () => {
			ui.hideCard()
			startFreshDay(false)
		},
		secondary: 'Keep rolling',
		onSecondary: () => {
			ui.hideCard()
			localStorage.setItem(GOLD_KEY, '1')
			startFreshDay(true)
		},
	})
}

function startFreshDay(gold: boolean): void {
	run = newRun()
	ui.setBest(run.best)
	ui.setHp(1)
	handles.setBallCondition(1)
	handles.setBallGold(gold || localStorage.getItem(GOLD_KEY) === '1')
	sfx.setDuck(0)
	spendSun(0)
	enterLobby()
}

/** The round against par, in golf's own notation. */
function roundToPar(): string {
	let rel = 0
	for (const [i, strokes] of run.card.entries()) {
		rel += strokes - (T.COURSE[i % T.COURSE.length]?.par ?? T.HOLE_PAR)
	}
	return rel === 0 ? 'level par' : rel > 0 ? `+${rel}` : `${rel}`
}

function endDay(title: string, sub: string): void {
	run.phase = 'DAY_END'
	sfx.play('sunset')
	sfx.setDuck(1)
	ui.hideDistance()
	ui.setHint('')
	ui.showCard({
		title,
		sub,
		stats: [
			['Holes played', `${run.card.length} of ${T.COURSE.length}`],
			['Round', roundToPar()],
			['Day score', Math.round(run.dayScore).toLocaleString()],
			['Best drive', `${Math.round(run.best)} m`],
			['Shots struck', String(run.shots)],
			['Ducks survived', String(run.dodges)],
			['Golfers turned down', String(run.skips)],
			['Condition left', `${Math.max(0, Math.round(run.hp))}%`],
			...(run.aces > 0 ? ([['Holes in one', String(run.aces)]] as [string, string][]) : []),
		],
		button: 'New day',
		// A fresh ball: the condition bar and the scuffs on the mesh both have to
		// come back with it, or the new day opens wearing the old one's damage.
		onClick: () => {
			ui.hideCard()
			startFreshDay(false)
		},
	})
}

// ── Damage ───────────────────────────────────────────────────────────────────

function damage(amount: number): void {
	if (amount <= 0) return
	run.hp = Math.max(0, run.hp - amount)
	// Every knock leaves a permanent bias. "My ball always slices now" is both
	// the mechanic and the joke.
	run.scuffBias += rng.range(T.SCUFF_BIAS_MIN, T.SCUFF_BIAS_MAX) * (rng.next() < 0.5 ? -1 : 1)
	ui.setHp(run.hp / T.HP_MAX)
	handles.setBallCondition(run.hp / T.HP_MAX)
}

// ── Impact ───────────────────────────────────────────────────────────────────

const CRACK: Record<string, 'crackFlush' | 'crackGood' | 'clank' | 'donk' | 'whiffAir'> = {
	FLUSH: 'crackFlush',
	GOOD: 'crackGood',
	THIN: 'clank',
	SHANK: 'donk',
	WHIFF: 'whiffAir',
}
const BURST: Record<string, 'flush' | 'good' | 'thin' | 'shank' | 'turf'> = {
	FLUSH: 'flush',
	GOOD: 'good',
	THIN: 'thin',
	SHANK: 'shank',
	WHIFF: 'turf',
}

function onImpact(ballYAtImpact: number): void {
	const c = resolveContact(ballYAtImpact, run.golfer, run.lie)
	const stamp = stampFor(c)

	sfx.play(CRACK[c.tier] ?? 'crackGood')
	sfx.setDuck(0)
	cameras.addTrauma(T.TRAUMA[c.tier])
	hitstopLeft = T.HITSTOP[c.tier]
	// The slow-motion the strike happened in, so the launch can ease back out of
	// it rather than snapping to full speed the instant the club connects.
	slowmoAtImpact = timescale
	handles.burst(BURST[c.tier] ?? 'turf', run.ballAt.x, sweetYFor(run.lie), run.ballAt.z)
	ui.showStamp(stamp.main, `${stamp.sub} · ${(c.offset * 100).toFixed(1)}cm`)

	if (c.tier === 'WHIFF') {
		onDodge()
		return
	}
	say(c.tier === 'FLUSH' ? 'flush' : c.tier === 'GOOD' ? 'good' : 'bad')

	damage(c.damage)
	run.lastTier = c.tier
	const launch = buildLaunch({
		contact: c,
		golfer: run.golfer,
		anger: run.swing.anger,
		goldenHour: goldenHour(),
		phone: run.swing.phoneRang,
		lie: run.lie,
		target: targetFromHere(),
		rng,
	})
	run.strokes++
	run.flight = B.createFlight(
		launch,
		run.hp,
		run.scuffBias + run.mud,
		(rng.next() * 0xffffffff) >>> 0,
		{ x: run.ballAt.x, z: run.ballAt.z, restHeight: run.lie.restY },
	)
	flightTime = 0
	airHold = null
	skipCued = false
	pendingLaunch = { contact: c }
	run.shots++
	spendSun(T.SUN_COST.shot)
}

function onDodge(): void {
	run.dodges++
	spendSun(T.SUN_COST.dodge)
	// Their phone rang. Whatever just happened is not something to be angry
	// about, and the game's only free duck is the one they handed you.
	if (run.swing.phoneRang) {
		say('whiff')
		ui.setHint('free one · they were on the phone')
		awaitingReset = true
		return
	}
	say('whiff')
	const events: SwingEvent[] = []
	const res = run.swing.registerWhiff(events)
	if (res.stormedOff) {
		sfx.play('stormOff')
		say('stormOff')
		handles.golfer.dropHat({ x: 0.9, z: -0.9 })
		tantrumT = 0
		ui.showStamp('WALKED OFF', `${run.golfer.name} had enough · one duck too far`)
		spendSun(T.SUN_COST.stormOff)
		run.phase = 'RESULT'
		holdTimer = 2.4
		return
	}
	run.nerve++
	const tier = run.swing.anger
	handles.golfer.setAnger(tier)
	if (tier >= 3) handles.golfer.dropHat({ x: 0.75, z: -0.8 })
	ui.setAnger(tier, run.swing.angerName)
	sfx.play('grunt', { rate: 0.85 + 0.12 * tier })
	const p = Math.min(1, (T.ANGER[tier] ?? T.ANGER[0]!).stormOff * run.golfer.temperMult)
	ui.setHint(
		p > 0
			? `${Math.round(p * 100)}% they walk if you duck again`
			: `${run.golfer.name} is still willing`,
	)
	// Let the follow-through and the tantrum play out; re-addressing happens on
	// the 'settled' event. The comedy beat and the pacing fix are the same beat.
	awaitingReset = true
}

/** The cut to the flight camera happens after the hitstop resolves, never before. */
function cutToFlight(): void {
	if (!pendingLaunch || !run.flight) return
	pendingLaunch = null
	run.phase = 'FLIGHT'
	handles.setTrailVisible(true)
	handles.clearTrail()
	handles.setBallSquash(0)
	cameras.setPhase('FLIGHT', camCtx())
	// Not the current timescale: by now the swing is in its follow-through and
	// the ramp has already returned 1, so reading it here would mean no ease-out
	// at all and the launch would snap straight to full speed.
	recoverFrom = slowmoAtImpact
	recoverLeft = T.LAUNCH_RECOVER
	ui.setHint('TAP to hop · HOLD to dive · TAP as you land to skip')
}

// ── Flight resolution ────────────────────────────────────────────────────────

// ── The hole ─────────────────────────────────────────────────────────────────

/** Which lie a surface gives you. Water and the tee mat are special cases. */
/** Distance and bearing from the ball to the flag: what club, and which way. */
function targetFromHere(): { dist: number; bearingDeg: number } {
	const dx = run.hole.pin.x - run.ballAt.x
	const dz = run.hole.pin.z - run.ballAt.z
	return {
		dist: Math.hypot(dx, dz),
		bearingDeg: (Math.atan2(dx, Math.max(0.001, dz)) * 180) / Math.PI,
	}
}

function lieFor(sfc: Surface): LieSpec {
	if (sfc === 'tee') return T.LIES.tee_mat!
	return T.LIES[sfc] ?? T.LIES.fairway!
}

function startHole(): void {
	run.hole = T.COURSE[(run.holeNo - 1) % T.COURSE.length]!
	B.setHole(run.hole)
	handles.buildHole(run.hole)
	run.strokes = 0
	run.ballAt = { x: run.hole.tee.x, z: run.hole.tee.z }
	run.lie = T.LIES.tee!
	run.mud = 0
	resetTeeBall(run.tee, run.lie)
}

/**
 * The golfer walks up to wherever you stopped and hits you again, carrying
 * their anger with them. That is the whole reason a lie matters: a bad shot is
 * no longer a score you shrug off, it is a position you now have to be hit out
 * of — and ducking mid-hole costs you your ride to the green.
 */
/**
 * The golfer walks up, and you get a moment to look at what you are being aimed
 * at before anyone swings.
 *
 * Two things were wrong without this. The rig only moved to the ball at the
 * instant the tee camera cut to it, so the golfer popped into frame rather than
 * being stood there already; and the dread cam faces *back* at him, which meant
 * the player never once saw the hole they were being hit down.
 */
function beginAim(): void {
	run.phase = 'AIM'
	awaitingReset = false
	pendingLaunch = null
	hitstopLeft = 0
	airHold = null
	resetTeeBall(run.tee, run.lie)
	// Stand him at the ball now, while nobody is looking at him yet.
	handles.setBallPos(run.ballAt.x, run.tee.y, run.ballAt.z)
	handles.setRigAt(run.ballAt.x, run.ballAt.z, run.lie.name === 'tee')
	handles.golfer.setSwingAngle(T.SWING_ANGLE_ADDRESS)
	handles.setTrailVisible(false)
	handles.clearTrail()
	ui.hideDistance()
	ui.hideBark()
	cameras.setPhase('AIM', camCtx())
	const d = Math.round(targetFromHere().dist)
	const w = run.hole.wind
	ui.setHint(
		w.speed > 0.5
			? `${d}m to the flag · wind ${w.speed.toFixed(0)}m/s`
			: `${d}m to the flag · ${run.lie.name}`,
	)
	aimLeft = T.AIM_TIME
	timescale = 1
	recoverLeft = 0
}

function nextShot(): void {
	run.phase = 'TEE'
	run.swing.beginAddress(false)
	cameras.setPhase('TEE', camCtx())
	ui.hideDistance()
	const ceiling = lieCeiling(run.lie)
	ui.setHint(
		ceiling === 'FLUSH'
			? `${run.lie.name} · stroke ${run.strokes + 1}`
			: `${run.lie.name} · you can't get better than ${ceiling.toLowerCase()} out of this`,
	)
	timescale = 1
	recoverLeft = 0
}

/** Score for a finished hole. An ace is worth more than the rest combined. */
function holeScore(strokes: number, par: number): number {
	// Relative to par, so a five on a par five is worth what a three on a par
	// three is. An ace is still worth more than anything else on the card.
	if (strokes === 1) return T.HOLE_SCORE[1] ?? 6000
	const rel = strokes - par
	return Math.max(T.HOLE_SCORE_MIN, Math.round(1600 * 0.55 ** Math.max(0, rel)))
}

function finishHole(holed: boolean): void {
	run.phase = 'RESULT'
	const strokes = holed ? run.strokes : T.MAX_STROKES
	run.card.push(strokes)
	if (holed) run.dayScore += holeScore(strokes, run.hole.par) * (1 + T.NERVE_MULT * run.nerve)
	run.holeNo++
	holdTimer = 2.8
	holeOver = true
	if (!holed) {
		ui.showStamp('PICKED UP', `${run.golfer.name} gave up on you`)
		return
	}
	const name =
		strokes === 1
			? 'ACE'
			: strokes === 2
				? 'BIRDIE'
				: strokes === 3
					? 'PAR'
					: `+${strokes - T.HOLE_PAR}`
	ui.showStamp(name, `hole ${run.holeNo - 1} in ${strokes}`)
}

function finishFlight(): void {
	const f = run.flight
	if (!f) return
	const result = B.scoreShot(f, run.lastTier, run.skims, run.nerve)
	run.lastResult = result
	run.dayScore += result.score
	damage(f.damage + T.HP_PER_FLIGHT)

	if (result.total > run.best) {
		run.best = result.total
		localStorage.setItem(BEST_KEY, String(Math.round(run.best)))
		ui.setBest(run.best)
	}

	run.phase = 'RESULT'

	// In the hole on the very first swing: the one thing the ball has ever
	// wanted, and the only ending that stops the game making jokes.
	if (result.holed && run.strokes === 1) {
		run.card.push(1)
		beginAceCeremony()
		return
	}

	if (result.holed) {
		sfx.play('plonk')
		finishHole(true)
		return
	}

	// Where the ball stopped is now a position, not a score. Water and out of
	// bounds cost a stroke and get dropped back on grass; everything else is
	// played exactly as it lies.
	const lost = result.surface === 'water' || result.surface === 'ob'
	if (lost) {
		run.strokes += T.PENALTY_STROKE
		run.ballAt = dropPoint(f)
		run.lie = lieFor(B.surfaceAt(run.ballAt.x, run.ballAt.z))
	} else {
		run.ballAt = { x: f.pos.x, z: f.pos.z }
		run.lie = lieFor(result.surface)
	}
	run.mud = Math.min(0.4, run.mud + run.lie.mud)

	if (run.strokes >= T.MAX_STROKES) {
		finishHole(false)
		return
	}

	ui.showDistance(result.total)
	holdTimer = T.WALK_TIME + 1.4
	holeOver = false
	cameras.setPhase('RESULT', camCtx())
	sfx.play('rest')
	// The nerve multiplier is only worth having if the player sees it get paid.
	const nerveTag =
		result.nerve > 0 ? ` · nerve ×${(1 + T.NERVE_MULT * result.nerve).toFixed(1)}` : ''
	ui.setHint(
		lost
			? `${result.surface === 'water' ? 'the pond keeps what it takes' : 'out of bounds'} · one stroke`
			: `${run.lie.name} · ${Math.round(result.distanceToPin)}m from the pin${nerveTag}`,
	)
}

/** Nearest playable grass to where the ball was lost. */
function dropPoint(f: FlightState): { x: number; z: number } {
	const h = run.hole
	const z = Math.max(4, Math.min(h.pin.z - 4, f.pos.z))
	// Dropped back onto the corridor's centreline, which bends with the hole.
	const t = Math.max(0, Math.min(1, z / Math.max(1, h.pin.z)))
	const centre = h.tee.x + (h.pin.x - h.tee.x) * t
	const x = Math.max(centre - h.fairwayHalfW + 2, Math.min(centre + h.fairwayHalfW - 2, f.pos.x))
	return { x, z }
}

// ── Simulation ───────────────────────────────────────────────────────────────

const swingEvents: SwingEvent[] = []
const teeEvents: TeeEvent[] = []

function simStep(dt: number): void {
	if (run.phase === 'TEE') {
		swingEvents.length = 0
		teeEvents.length = 0
		const yBefore = run.tee.y
		run.swing.step(dt, swingEvents)
		stepTeeBall(run.tee, dt, holding(), teeEvents, run.lie)
		// Where the ball was when the club actually arrived, which is somewhere
		// inside this step rather than at the end of it.
		const yAtImpact = yBefore + (run.tee.y - yBefore) * run.swing.impactFrac

		for (const e of teeEvents) {
			if (e === 'squat') sfx.play('squat')
			else if (e === 'hop') sfx.play('hop')
		}
		for (const e of swingEvents) {
			switch (e.kind) {
				case 'squeak':
					sfx.play('squeak', { rate: 0.9 + 0.1 * run.swing.anger })
					break
				case 'backswing':
					sfx.play('whoosh')
					say('address')
					break
				case 'phone':
					// Somebody's phone in the silence at the top of the backswing.
					sfx.play('squeak', { rate: 1.9 })
					say('phone')
					ui.setHint('their phone · this one is free')
					break
				case 'top':
					// The duck itself is driven from the swing stage each frame; the
					// event only exists so the mix has a beat to land on.
					break
				case 'tick':
					sfx.play('tick')
					break
				case 'tickFinal':
					sfx.play('tickFinal')
					break
				case 'impact':
					onImpact(yAtImpact)
					break
				case 'settled':
					if (awaitingReset) {
						awaitingReset = false
						run.swing.beginAddress(false)
						resetTeeBall(run.tee, run.lie)
					}
					break
				default:
					break
			}
		}

		handles.setBallPos(run.ballAt.x, run.tee.y, run.ballAt.z)
		handles.setBallSquash(run.tee.squash)
		handles.golfer.setSwingAngle(run.swing.swingAngle)
		return
	}

	if (run.phase === 'FLIGHT' || run.phase === 'ROLLOUT') {
		const f = run.flight
		if (!f) return
		const events = B.stepFlight(f, dt, flightTime)
		flightTime += dt
		for (const e of events) {
			switch (e.kind) {
				case 'bounce':
					skipCued = false
					sfx.play(e.surface === 'cartpath' ? 'bounceHard' : 'bounceTurf', {
						gain: Math.min(1, e.speed / 30),
					})
					handles.burst(e.surface === 'cartpath' ? 'sparks' : 'turf', e.pos.x, e.pos.y, e.pos.z)
					if (e.surface === 'cartpath') cameras.addTrauma(0.35)
					if (f.bounces >= 2 && cameras.phase === 'FLIGHT') cameras.setPhase('PLANT', camCtx())
					break
				case 'skip':
					skipCued = false
					sfx.play('skim', { rate: 1.15 })
					handles.burst('turf', e.pos.x, e.pos.y, e.pos.z)
					ui.showStamp('SKIP!', `${run.flight?.skips ?? 0} in a row`)
					break
				case 'skim':
					run.skims++
					sfx.play('skim')
					handles.burst('splash', e.pos.x, e.pos.y, e.pos.z)
					break
				case 'splash':
					sfx.play('splash')
					handles.burst('splash', e.pos.x, e.pos.y, e.pos.z)
					break
				case 'ob':
					sfx.play('bounceTurf', { gain: 0.5 })
					break
				case 'holed':
					break
				case 'roll':
					if (cameras.phase === 'FLIGHT') cameras.setPhase('PLANT', camCtx())
					run.phase = 'ROLLOUT'
					break
				case 'rest':
					// Park the mesh where the ball actually stopped before handing
					// over — nothing moves it again once the phase changes.
					handles.setBallPos(f.pos.x, f.pos.y, f.pos.z)
					finishFlight()
					return
				default:
					break
			}
		}
		handles.setBallPos(f.pos.x, f.pos.y, f.pos.z)
		if (!f.rolling) handles.pushTrail(f.pos.x, f.pos.y, f.pos.z)
		ui.tickDistance(f.total)
		return
	}
}

// ── Frame ────────────────────────────────────────────────────────────────────

function camCtx() {
	const f = run.flight
	// Standing on the ball, wherever the ball actually is. This was pinned to the
	// world origin, so from the second stroke onward every camera that anchors on
	// the ball stayed behind at the tee staring down an empty fairway while the
	// golfer swung at you a hundred and fifty metres away — he only appeared once
	// the chase cam picked the flight up.
	const onGround = run.phase === 'AIM' || run.phase === 'TEE' || run.phase === 'LOBBY'
	const ballPos = onGround || !f ? { x: run.ballAt.x, y: run.tee.y, z: run.ballAt.z } : f.pos
	const vel = f && !onGround ? f.vel : { x: 0, y: 0, z: 0 }
	return {
		ball: ballPos,
		pin: run.hole.pin,
		vel,
		tToImpact: run.phase === 'TEE' ? run.swing.tToImpact : Number.POSITIVE_INFINITY,
		timescale,
	}
}

function updateTimescale(rawDt: number): void {
	if (recoverLeft > 0) {
		recoverLeft = Math.max(0, recoverLeft - rawDt)
		const k = 1 - recoverLeft / T.LAUNCH_RECOVER
		timescale = recoverFrom + (1 - recoverFrom) * k
		return
	}
	if (run.phase === 'TEE') {
		timescale = run.swing.rampTimescale
		// Duck from the top of the backswing all the way through the downswing, so
		// the crack lands on quiet. Keyed off the swing stage rather than off the
		// timescale: the ramp has not started during the top pause, so a
		// timescale-derived duck would run at full volume through the one silence
		// the whole telegraph is built on — and would overwrite it every frame.
		const s = run.swing.stage
		sfx.setDuck(s === 'TOP' || s === 'DOWNSWING' ? 1 : 0)
		return
	}
	// Never fast-forward while a verb could still save the ball. Once it is
	// rolling there is nothing left to play, so a lost cause stops being
	// something the player has to sit through; before that, a hop or a skip off
	// the next bounce is a live decision and skipping it is worse than watching.
	if (
		run.phase === 'FLIGHT' &&
		run.flight &&
		run.flight.rolling &&
		run.flight.nudgesLeft === 0 &&
		B.isDoomed(run.flight)
	) {
		timescale = T.DOOMED_TIMESCALE
		return
	}
	timescale = 1
}

let spinAngle = 0

function frame(nowMs: number): void {
	const rawDt = Math.min((nowMs - lastFrame) / 1000, T.MAX_FRAME)
	lastFrame = nowMs

	// Unscaled clock: juice keeps moving through hitstop, or it reads as a bug.
	handles.stepEffects(rawDt)
	cameras.update(rawDt, camCtx())

	if (tantrumT !== null) {
		tantrumT += rawDt / TANTRUM_TIME
		handles.golfer.setTantrum(Math.min(1, tantrumT))
		if (tantrumT >= 1) tantrumT = null
	}

	if (aceT !== null) {
		aceT += rawDt
		if (!acePiano && aceT >= ACE_SILENCE) {
			acePiano = true
			// The world comes back first, then the note.
			sfx.setDuck(0)
			sfx.play('piano')
		}
		if (aceT >= ACE_TOTAL) {
			aceT = null
			showAceCard()
		}
	}

	// Rolling away, which is only possible while nothing is swinging at you.
	if (run.phase === 'TEE' && run.swing.stage === 'ADDRESS' && inputDown) {
		addressHeld += rawDt
		if (addressHeld >= T.ROLL_AWAY_HOLD) rollAway()
	} else if (run.phase !== 'TEE' || run.swing.stage !== 'ADDRESS') {
		addressHeld = 0
	}

	// Airborne verbs live on the unscaled clock: they are input, not simulation.
	if (airHold !== null && run.flight) {
		airHold += rawDt
		if (airHold >= T.DIVE_HOLD) B.setDive(run.flight, true)
	}
	if (run.phase === 'FLIGHT' && run.flight && !run.flight.rolling) {
		// One tick as the landing window opens, for the same reason the swing has
		// a telegraph: a timing verb with no tell is a guess.
		const tGround = B.timeToGround(run.flight)
		if (!skipCued && tGround <= T.SKIP_WINDOW * 2.2) {
			skipCued = true
			sfx.play('tick', { rate: 0.85, gain: 0.5 })
		}
	}

	// The bucket runs on unscaled time — it is a decision, not a simulation.
	if (run.phase === 'LOBBY') {
		// The veterans in the bucket have opinions. They are allowed to speak
		// because the player-ball is not.
		bucketChat -= rawDt
		if (bucketChat <= 0) {
			ui.showBark(bucketLine(rng.next()), 'bucket')
			bucketChat = rng.range(6, 11)
		}
		if (!lobbyArmed && !inputDown) lobbyArmed = true
		if (lobbyArmed && inputDown) {
			lobbyHeld += rawDt
			ui.setLobbyHold(lobbyHeld / T.LOBBY_HOLD_TIME)
			if (lobbyHeld >= T.LOBBY_HOLD_TIME) skipGolfer()
		}
	}

	if (aimLeft > 0) {
		aimLeft = Math.max(0, aimLeft - rawDt)
		if (aimLeft === 0) nextShot()
	}

	if (holdTimer > 0) {
		holdTimer = Math.max(0, holdTimer - rawDt)
		if (holdTimer === 0 && run.phase === 'RESULT') {
			// A finished hole sends you back to the bucket for a new golfer; an
			// unfinished one just means they have walked up to you.
			if (holeOver) nextEncounter()
			else beginAim()
		}
	}

	updateTimescale(rawDt)

	let scaled = rawDt * timescale
	if (hitstopLeft > 0) {
		hitstopLeft = Math.max(0, hitstopLeft - rawDt)
		scaled = 0
	}

	accumulator = Math.min(accumulator + scaled, T.MAX_FRAME)
	while (accumulator >= T.SIM_DT) {
		simStep(T.SIM_DT)
		accumulator -= T.SIM_DT
		// A strike that just landed freezes time immediately: the rest of this
		// frame's budget must not keep advancing the swing through the stop.
		if (hitstopLeft > 0) break
	}
	// The cut waits for the stop to expire; a tier with no stop at all cuts on
	// the same frame rather than waiting for a branch that will never run.
	if (pendingLaunch && hitstopLeft === 0) cutToFlight()

	// Ball spin is cosmetic but it makes the face tumble, which makes spin legible.
	const f = run.flight
	if (f && run.phase !== 'TEE') {
		const speed = Math.hypot(f.vel.x, f.vel.y, f.vel.z)
		spinAngle += speed * rawDt * 0.9
		handles.setBallSpin(spinAngle, { x: 1, y: 0.15, z: 0 })
		sfx.setWind(Math.min(1, speed / 80))
	} else {
		sfx.setWind(0)
	}

	// The golfer's-eye information: which hole, how many so far, how far is left,
	// and where the wind is going. Cached inside the Ui, so pushing it every
	// frame costs nothing.
	if (run.phase !== 'DAY_END') {
		ui.setScore({
			holeNo: run.hole.no,
			holeName: run.hole.name,
			par: run.hole.par,
			strokes: run.strokes,
			toPin: targetFromHere().dist,
			card: run.card.map((strokes, i) => ({
				par: T.COURSE[i % T.COURSE.length]?.par ?? T.HOLE_PAR,
				strokes,
			})),
			wind: run.hole.wind,
		})
	}

	if (debugOn) ui.setDebug(debugLines())

	renderer.render(handles.scene, cameras.camera)
}

function debugLines(): string[] {
	const head = clubheadAt(run.swing.swingAngle)
	const offsetNow = run.tee.y - sweetYFor(run.lie)
	const tti = run.swing.tToImpact
	// In the bucket the golfer that matters is the one on the mat, not the one
	// whose encounter just ended.
	const onMat = run.phase === 'LOBBY' ? (run.queue[0]?.name ?? '--') : run.golfer.name
	return [
		`phase      ${run.phase}`,
		`golfer     ${onMat} (anger ${run.swing.anger} ${run.swing.angerName})`,
		...(run.phase === 'LOBBY'
			? [`queue      ${run.queue.map((q) => q.name).join(', ')}  skips ${run.skips}`]
			: []),
		`stage      ${run.swing.stage}  t-impact ${Number.isFinite(tti) ? tti.toFixed(3) : '--'}`,
		`timescale  ${timescale.toFixed(3)}  hitstop ${hitstopLeft.toFixed(3)}`,
		`charge     ${run.tee.charge.toFixed(3)}  (flush ${flushCharge(run.lie).toFixed(3)})`,
		`apex       ${apexForCharge(run.tee.charge, run.lie).toFixed(4)}  sweet ${sweetYFor(run.lie).toFixed(4)}`,
		`ball y     ${run.tee.y.toFixed(4)}  offset ${(offsetNow * 100).toFixed(2)}cm`,
		`club y/z   ${head.y.toFixed(3)} / ${head.z.toFixed(3)}`,
		`hp         ${run.hp.toFixed(0)}  bias ${run.scuffBias.toFixed(3)}`,
		`sun        ${run.sunUsed}/${T.SUN_NOTCHES}${goldenHour() ? ' GOLDEN' : ''}`,
		`backend    ${forceWebGL ? 'webgl2 (forced)' : 'webgpu if available'}`,
	]
}

// ── Debug hook ───────────────────────────────────────────────────────────────
//
// Only under ?debug. The tee verb is a timing mechanic, so being able to drive
// it from a script — press at exactly this many milliseconds before impact — is
// the only way to check that a flush strike is actually reachable rather than
// merely plausible on paper.
if (debugOn) {
	Object.defineProperty(window, '__dimples', {
		value: {
			press,
			release,
			/** Run the ceremony without waiting on a one-in-fourteen-runs event. */
			ace: beginAceCeremony,
			get state() {
				return {
					phase: run.phase,
					stage: run.swing.stage,
					// In the bucket the golfer that matters is the one on the mat.
					golfer: run.phase === 'LOBBY' ? (run.queue[0]?.name ?? run.golfer.name) : run.golfer.name,
					anger: run.swing.anger,
					tToImpact: run.swing.tToImpact,
					charge: run.tee.charge,
					ballY: run.tee.y,
					offset: run.tee.y - T.SWEET_Y,
					timescale,
					hp: run.hp,
					sun: run.sunUsed,
					lastTier: run.lastTier,
					flushCharge: flushCharge(run.lie),
					// The airborne verbs, so their windows can be driven and tuned.
					nudgesLeft: run.flight?.nudgesLeft ?? 0,
					diving: run.flight?.diving ?? false,
					skips: run.flight?.skips ?? 0,
					rolling: run.flight?.rolling ?? false,
					timeToGround: run.flight ? B.timeToGround(run.flight) : null,
				}
			},
		},
		configurable: true,
	})
}

// ── Boot ─────────────────────────────────────────────────────────────────────

ui.setBest(run.best)
ui.setHp(1)
spendSun(0)
// The day opens at the bucket: the first thing the ball ever does is choose.
enterLobby()
sfx.startAmbience()
renderer.setAnimationLoop(frame)
